/**
 * Video error categories for user-friendly error messages.
 */
export const VIDEO_ERROR_CATEGORY = {
  NETWORK: 'network',
  CAMERA: 'camera',
  SERVER: 'server',
  GENERIC: 'generic',
} as const;

export type VideoErrorCategory = (typeof VIDEO_ERROR_CATEGORY)[keyof typeof VIDEO_ERROR_CATEGORY];

/**
 * Maps error categories to i18n keys.
 */
export const VIDEO_ERROR_I18N_KEYS: Record<VideoErrorCategory, string> = {
  [VIDEO_ERROR_CATEGORY.NETWORK]: 'video.error.network',
  [VIDEO_ERROR_CATEGORY.CAMERA]: 'video.error.camera',
  [VIDEO_ERROR_CATEGORY.SERVER]: 'video.error.server',
  [VIDEO_ERROR_CATEGORY.GENERIC]: 'video.error.generic',
};

interface ErrorPattern {
  pattern: RegExp;
  category: VideoErrorCategory;
}

/**
 * Error patterns mapped to categories.
 * Order matters - first match wins.
 */
const ERROR_PATTERNS: ErrorPattern[] = [
  // Network errors - WebRTC/ICE failures, NAT issues
  { pattern: /WebRTC connection failed/i, category: VIDEO_ERROR_CATEGORY.NETWORK },
  { pattern: /NAT|firewall/i, category: VIDEO_ERROR_CATEGORY.NETWORK },
  { pattern: /Connection failed$/i, category: VIDEO_ERROR_CATEGORY.NETWORK },
  { pattern: /ICE.*failed/i, category: VIDEO_ERROR_CATEGORY.NETWORK },

  // Camera errors - missing config, camera offline
  { pattern: /Missing video configuration/i, category: VIDEO_ERROR_CATEGORY.CAMERA },
  { pattern: /No local description available/i, category: VIDEO_ERROR_CATEGORY.CAMERA },

  // Server errors - SDP errors, server timeouts
  { pattern: /No answer SDP received from server/i, category: VIDEO_ERROR_CATEGORY.SERVER },
  {
    pattern: /Invalid answer SDP format received from server/i,
    category: VIDEO_ERROR_CATEGORY.SERVER,
  },
  { pattern: /SDP/i, category: VIDEO_ERROR_CATEGORY.SERVER },
  { pattern: /timeout/i, category: VIDEO_ERROR_CATEGORY.SERVER },
];

export interface CategorizedVideoError {
  category: VideoErrorCategory;
  technicalMessage: string;
}

/**
 * Categorizes a video error into a user-friendly category.
 * Logs technical details to console for developers.
 */
export function categorizeVideoError(
  errorMessage: string,
  originalError: Error | null = null
): CategorizedVideoError {
  const technicalMessage = errorMessage || originalError?.message || 'Unknown error';

  // Find matching category
  let category: VideoErrorCategory = VIDEO_ERROR_CATEGORY.GENERIC;
  for (const { pattern, category: cat } of ERROR_PATTERNS) {
    if (pattern.test(technicalMessage)) {
      category = cat;
      break;
    }
  }

  // Log technical details for developers
  console.error('[VideoError]', {
    category,
    technicalMessage,
    timestamp: new Date().toISOString(),
    ...(originalError && { stack: originalError.stack }),
  });

  return {
    category,
    technicalMessage,
  };
}
